import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GavelIcon from "@mui/icons-material/Gavel";

import { FilterableDataGrid } from "../common/FilterableDataGrid";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import {
  ModerationQueueFlag,
  ModerationReviewResource,
} from "../../api/moderation";
import { useModerationStore } from "../../store/moderationStore";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { formatDateTime, truncateText } from "./utils";
import { SuspendUploadsDialog } from "./SuspendUploadsDialog";

/**
 * The review queue for flags a human has to decide on.
 *
 * Deliberately not the chat flags list beside it. That one is a record of what
 * Comprehend already did — nothing ever changes a chat flag's state — while
 * every row here is work. The two also read `status` in opposite directions, so
 * the vocabulary is kept apart: nothing in this view says FLAGGED or DELETED.
 */
export const ImageReviewList: React.FC = () => {
  const {
    reviewFlags,
    loadingReviewFlags,
    reviewNextToken,
    loadReviewFlags,
    reviewError,
  } = useModerationStore();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const { withLoading } = useGlobalLoading();

  const [confirming, setConfirming] = useState<{
    flag: ModerationQueueFlag;
    verdict: "approve" | "reject";
  } | null>(null);
  const [suspending, setSuspending] = useState<ModerationQueueFlag | null>(null);
  /** The flag a moderator is about to place under legal hold. */
  const [holding, setHolding] = useState<ModerationQueueFlag | null>(null);
  /**
   * Whether rejecting should also take the member's published picture down.
   *
   * Ticked by default, because leaving a reported picture up is rarely what a
   * moderator means by "reject" — and until this existed, rejecting a report
   * changed nothing a member could see. The default lives here rather than on
   * the server: a client that predates the field must not start removing
   * pictures because it omitted one.
   */
  const [removePicture, setRemovePicture] = useState(true);

  useEffect(() => {
    loadReviewFlags("PENDING");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The store asks for pictures and reports by kind, so chat flags never arrive
  // here — they belong to the list next door, which describes `status` the
  // opposite way round. Filtering them out client-side instead would mean a
  // page of chat could leave this view empty while pictures wait unreviewed.
  const rows = reviewFlags;

  /**
   * Whether approving this flag publishes anything.
   *
   * Only an image the automated gate refused has an object behind it. A member
   * reporting someone's picture writes the same content type but carries the
   * reported category instead, and a conduct report has no artifact at all —
   * approving either resolves the row and publishes nothing.
   */
  const publishesOnApprove = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE" && flag.source === "REKOGNITION";

  /**
   * Whether this flag is about a picture the member may have live right now.
   *
   * The mirror of `publishesOnApprove`, and it mirrors the server's own guard: a
   * picture flagged by a person passed the automated gate and went public, which
   * is the only case with anything to take down. An automated flag's image was
   * never published, and a conduct report is not about a picture at all.
   */
  const canRemoveOnReject = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE" &&
    (flag.source === "MEMBER" || flag.source === "GUEST") &&
    // A reporter who named someone in free text rather than from a roster row
    // leaves no member to act on, and the server can only take a picture down
    // by member. Offering the box here would promise a removal that cannot
    // happen and then report it as done. The suspend button below is gated on
    // the same field for the same reason.
    !!flag.senderId;

  /**
   * Whether this flag has a picture that can be placed under legal hold.
   *
   * Any picture flag, from either writer: an automated refusal names its
   * retained object, and a report names a member whose picture is live or was
   * taken down. The server decides which copy to hold and refuses with a 409
   * when there is nothing — a report naming nobody, say — so this only hides
   * the button where it could never apply.
   */
  const canHold = (flag: ModerationQueueFlag) =>
    flag.contentType === "PROFILE_PICTURE";

  const hold = async (flag: ModerationQueueFlag) => {
    try {
      const result = await withLoading(ModerationReviewResource.preserve(flag.id));
      await loadReviewFlags("PENDING");
      showSnackbar(
        result.alreadyHeld
          ? "Already under legal hold — nothing changed"
          : "Placed under legal hold. The member has not been told.",
        "success"
      );
    } catch (err: any) {
      // 409 means there was nothing to hold — the report names nobody, or the
      // member has no picture live or retained. The server's reason says which.
      showSnackbar(err?.message || "Failed to place the hold", "error");
      await loadReviewFlags("PENDING");
    } finally {
      setHolding(null);
    }
  };

  const decide = async (
    flag: ModerationQueueFlag,
    verdict: "approve" | "reject"
  ) => {
    try {
      // Typed explicitly: approve's result is a subtype of reject's, so inference
      // would collapse the union to reject's shape and lose `publicUrl`.
      const result = await withLoading<
        Awaited<ReturnType<typeof ModerationReviewResource.approve>>
      >(
        verdict === "approve"
          ? ModerationReviewResource.approve(flag.id)
          : ModerationReviewResource.reject(
              flag.id,
              undefined,
              // Only ever sent where it applies, so a stale tick from a previous
              // dialog cannot reach a flag it was not meant for.
              canRemoveOnReject(flag) && removePicture
            )
      );
      await loadReviewFlags("PENDING");
      showSnackbar(
        verdict === "approve"
          ? result.publicUrl
            ? "Approved — the picture is now live"
            : "Approved"
          : canRemoveOnReject(flag) && removePicture
            // Deliberately not "the picture was removed". The approve branch
            // above can assert its outcome because the response carries
            // `publicUrl`; a reject carries no removal signal at all, so the
            // server skipping the takedown — the member cleared it first, a
            // partial sweep that does not fail the request — would otherwise
            // read as a completed one.
            ? "Rejected — removal requested"
            : "Rejected — nothing was published",
        "success"
      );
    } catch (err: any) {
      // 409 is the ordinary case, not a fault: another moderator got there
      // first, or the member has published a different picture since. Say which
      // rather than showing a raw status.
      showSnackbar(err?.message || `Failed to ${verdict} the flag`, "error");
      await loadReviewFlags("PENDING");
    } finally {
      setConfirming(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "thumbnailUrl",
      headerName: "Image",
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;
        // Only an automated refusal names an object. A reported picture carries
        // the reported category in `content`, so there is nothing to show.
        if (!flag.thumbnailUrl) {
          return (
            <Tooltip title="No image — this was reported, not scanned">
              <Avatar variant="rounded" sx={{ width: 40, height: 40 }} />
            </Tooltip>
          );
        }
        return (
          <Avatar
            variant="rounded"
            src={flag.thumbnailUrl}
            alt="Flagged picture"
            sx={{ width: 40, height: 40 }}
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Flagged",
      width: 165,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "source",
      headerName: "Raised by",
      width: 130,
      renderCell: (params) => {
        const source = params.value as ModerationQueueFlag["source"];
        const label =
          source === "REKOGNITION"
            ? "Automated"
            : source === "MEMBER"
              ? "Member"
              : source === "GUEST"
                ? "Guest"
                : source;
        return <Chip label={label} size="small" sx={{ fontSize: "0.7rem" }} />;
      },
    },
    {
      field: "senderId",
      headerName: "Member",
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <span>{truncateText(params.value, 14)}</span>
          </Tooltip>
        ) : (
          // A free-text report names its subject in prose, not as an id.
          <Tooltip title="Named in the report, not linked to an account">
            <span>—</span>
          </Tooltip>
        ),
    },
    {
      field: "categories",
      headerName: "Categories",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const categories: string[] = params.value || [];
        if (categories.length === 0) return "—";
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {categories.slice(0, 2).map((category) => (
              <Chip
                key={category}
                label={category}
                size="small"
                sx={{ fontSize: "0.7rem", height: "20px" }}
              />
            ))}
            {categories.length > 2 && (
              <Tooltip title={categories.slice(2).join(", ")}>
                <Chip
                  label={`+${categories.length - 2}`}
                  size="small"
                  sx={{ fontSize: "0.7rem", height: "20px" }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "confidence",
      headerName: "Confidence",
      width: 130,
      renderCell: (params) => {
        // A member's report records no score by design — it is an assertion,
        // not a probability. `formatConfidence` would render NaN% here.
        if (params.value == null) {
          return (
            <Tooltip title="Reported by a person; there is no model score">
              <Typography variant="body2" color="text.secondary">
                Reported
              </Typography>
            </Tooltip>
          );
        }
        return `${(params.value * 100).toFixed(1)}%`;
      },
    },
    {
      field: "actions",
      headerName: "Decision",
      width: 210,
      sortable: false,
      renderCell: (params) => {
        const flag = params.row as ModerationQueueFlag;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip
              title={
                publishesOnApprove(flag)
                  ? "The content is acceptable — publishes the picture"
                  : "The content is acceptable — resolves the report, publishes nothing"
              }
            >
              <span>
                <Button
                  size="small"
                  startIcon={<CheckIcon />}
                  onClick={() => setConfirming({ flag, verdict: "approve" })}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                publishesOnApprove(flag)
                  ? "Not acceptable — it stays unpublished"
                  : canRemoveOnReject(flag)
                    ? "Upholds the report, and offers to take the picture down"
                    : "Upholds the report; nothing about the content changes"
              }
            >
              <span>
                <Button
                  size="small"
                  color="error"
                  startIcon={<BlockIcon />}
                  // Re-ticked every time the dialog opens. Carrying an untick
                  // from the previous row would leave a picture up that the
                  // moderator believed they had removed.
                  onClick={() => {
                    setRemovePicture(true);
                    setConfirming({ flag, verdict: "reject" });
                  }}
                >
                  Reject
                </Button>
              </span>
            </Tooltip>
            {canHold(flag) &&
              (flag.preservedAt ? (
                <Tooltip
                  title={`Under legal hold since ${formatDateTime(flag.preservedAt)}. Only a developer can release it.`}
                >
                  <Chip
                    label="Held"
                    size="small"
                    color="secondary"
                    icon={<GavelIcon />}
                    sx={{ alignSelf: "center" }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Legal hold: keep a locked copy for law enforcement. Removes nothing, tells nobody.">
                  <span>
                    <Button
                      size="small"
                      color="secondary"
                      aria-label="Legal hold"
                      onClick={() => setHolding(flag)}
                    >
                      <GavelIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
              ))}
            {flag.senderId && (
              <Tooltip title="Bar this member from uploading">
                <span>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => setSuspending(flag)}
                  >
                    <PersonOffIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      {reviewError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reviewError}
        </Alert>
      )}

      {reviewNextToken && (
        // The grid pages client-side, so it cannot fetch the rest. Saying so is
        // better than showing a partial queue that looks complete.
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the most recent {rows.length}. There are older flags still
          pending than fit on this page.
        </Alert>
      )}

      {!loadingReviewFlags &&
        rows.length === 0 &&
        !reviewError &&
        !reviewNextToken && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Nothing waiting for review.
          </Alert>
        )}

      <FilterableDataGrid
        rows={rows}
        columns={columns}
        filters={[
          {
            label: "Kind",
            field: "contentType",
            options: [
              { value: "ALL", label: "All" },
              { value: "PROFILE_PICTURE", label: "Pictures" },
              { value: "USER", label: "Conduct reports" },
            ],
          },
          {
            label: "Raised by",
            field: "source",
            options: [
              { value: "ALL", label: "All" },
              { value: "REKOGNITION", label: "Automated" },
              { value: "MEMBER", label: "Member" },
              { value: "GUEST", label: "Guest" },
            ],
          },
        ]}
        searchFields={["senderId", "content", "id"]}
        searchPlaceholder="Search by member, content, or flag id..."
        height={600}
        pageSize={25}
      />

      <ConfirmActionDialog
        open={confirming !== null}
        title={
          confirming?.verdict === "approve" ? "Approve this?" : "Reject this?"
        }
        message={
          confirming?.verdict === "approve" ? (
            confirming.flag && publishesOnApprove(confirming.flag) ? (
              <>
                The picture will be published to this member's profile. If they
                have published a different picture since this was flagged, the
                approval is refused rather than overwriting it.
              </>
            ) : (
              <>
                Records that a human found this acceptable. Nothing is published
                — there is no image behind this flag.
              </>
            )
          ) : confirming?.flag && canRemoveOnReject(confirming.flag) ? (
            <>
              This picture is live on the member&apos;s profile — upholding the
              report on its own does not change that.
              <FormControlLabel
                sx={{ display: "flex", mt: 1.5 }}
                control={
                  <Checkbox
                    checked={removePicture}
                    onChange={(event) =>
                      setRemovePicture(event.target.checked)
                    }
                  />
                }
                label="Also remove the picture, and tell the member"
              />
              {removePicture ? (
                <Typography variant="body2" color="text.secondary">
                  It comes off their profile, the directory and the in-call
                  roster. A copy is kept so the decision can be revisited, and
                  they are emailed. It does not stop them uploading another —
                  bar uploads as well if that is what you mean.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  The decision is recorded and the picture stays up. Leave this
                  unticked only if the picture is acceptable and the report is
                  not.
                </Typography>
              )}
            </>
          ) : (
            <>
              The content stays unpublished. Nothing is deleted: what was flagged
              is kept so the decision can be revisited.
            </>
          )
        }
        confirmLabel={
          confirming?.verdict === "approve" ? "Approve" : "Reject"
        }
        onClose={() => setConfirming(null)}
        onConfirm={() =>
          confirming && decide(confirming.flag, confirming.verdict)
        }
      />

      <ConfirmActionDialog
        open={holding !== null}
        title="Place this picture under legal hold?"
        message={
          <>
            A copy of the picture is kept somewhere nothing can delete, and the
            flag is marked as held. Nothing is removed and the member is{" "}
            <strong>not told</strong> — not now, and not if you reject and remove
            the picture afterwards. Use this only for material you are
            escalating under the runbook. Only a developer can release a hold.
          </>
        }
        confirmLabel="Place hold"
        onClose={() => setHolding(null)}
        onConfirm={() => holding && hold(holding)}
      />

      <SuspendUploadsDialog
        userId={suspending?.senderId}
        onClose={() => setSuspending(null)}
        onDone={(message) => {
          setSuspending(null);
          showSnackbar(message, "success");
        }}
      />

      {SnackbarNode}
    </>
  );
};
